import { styled } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { chartsGridClasses } from '@mui/x-charts/ChartsGrid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import { Container as CardContainer } from '@/components/Card/styles';

export const SpectrumChartBox = styled(Stack)`
  gap: 6px;
  width: 100%;
  min-width: 0;
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const SpectrumChartContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  minWidth: 0,
  flex: 1,
  height: 320,
  minHeight: 280,
  [theme.breakpoints.up('sm')]: {
    height: 380,
    minHeight: 340,
  },
  [theme.breakpoints.up('md')]: {
    height: 430,
    minHeight: 390,
  },
  [theme.breakpoints.up('lg')]: {
    height: 470,
    minHeight: 430,
  },
  [theme.breakpoints.up('xl')]: {
    height: 520,
    minHeight: 480,
  },
}));

export const GradientDefsSvg = styled('svg')`
  width: 0;
  height: 0;
  position: absolute;
`;

export const NetworkLegendColor = styled('span')<{ background: string }>`
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ background }) => background};
  box-shadow: 0 0 6px ${({ background }) => background};
  flex-shrink: 0;
`;

export const StyledSpectrumLineChart = styled(LineChart)<{
  areastyles?: Record<string, React.CSSProperties>;
}>(({ theme, areastyles }) => ({
  width: '100% !important',
  height: '100% !important',
  [`& .${axisClasses.root}`]: {
    [`.${axisClasses.tick}, .${axisClasses.line}`]: {
      stroke: 'rgba(255,255,255,0.2)',
      strokeWidth: 1,
    },
    [`.${axisClasses.tickLabel}`]: {
      fill: theme.palette.white['06'],
      fontSize: 11,
      fontWeight: 600,
    },
  },
  [`& .${chartsGridClasses.line}`]: {
    strokeDasharray: '4 4',
    stroke: 'rgba(255,255,255,0.1)',
  },
  // Hide any built-in MUI X-Charts top legend
  '& .MuiChartsLegend-root, & .MuiChartsLegend-series, & .MuiChartsLegend-column, & g.MuiChartsLegend-root, & svg.MuiChartsLegend-root': {
    display: 'none !important',
    visibility: 'hidden !important',
    opacity: '0 !important',
    pointerEvents: 'none !important',
  },
  // Ensure lines are drawn above areas
  '& .MuiAreaElement-root': {
    zIndex: 1,
  },
  '& .MuiLineElement-root': {
    zIndex: 2,
    strokeWidth: 2.5,
  },
  ...areastyles,
}));

export const BottomLegendContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '10px 14px',
  borderRadius: '12px',
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.25)'
      : 'rgba(0, 0, 0, 0.03)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.06)',
}));

export const LegendChipItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 8px',
  borderRadius: '8px',
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(0, 0, 0, 0.04)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.07)'
      : '1px solid rgba(0, 0, 0, 0.07)',
  transition: 'all 0.2s ease',
  cursor: 'default',
  '&:hover': {
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.09)'
        : 'rgba(0, 0, 0, 0.08)',
    borderColor: theme.palette.primary['01'],
  },
}));

export const LegendBadge = styled('span')(({ theme }) => ({
  fontSize: '10px',
  fontWeight: 600,
  padding: '1px 5px',
  borderRadius: '4px',
  background: 'rgba(255, 255, 255, 0.08)',
  color: theme.palette.white['06'],
  letterSpacing: '0.3px',
}));

export const HiddenSSIDLabel = styled('span')`
  font-style: italic;
  opacity: 0.6;
  font-size: 12px;
`;

export const FilterBarContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '10px',
  padding: '8px 12px',
  borderRadius: '12px',
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.22)'
      : 'rgba(0, 0, 0, 0.03)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.06)',
}));

export const FilterToggleChip = styled('button')<{ active?: boolean }>(
  ({ theme, active }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    border: active
      ? `1px solid ${theme.palette.primary.main}`
      : theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.1)',
    background: active
      ? theme.palette.mode === 'dark'
        ? 'rgba(0, 207, 255, 0.15)'
        : 'rgba(0, 207, 255, 0.12)'
      : theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(0, 0, 0, 0.04)',
    color: active
      ? theme.palette.primary.main
      : theme.palette.white['06'],
    transition: 'all 0.2s ease',
    '&:hover': {
      background:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.08)',
      borderColor: theme.palette.primary.main,
    },
  })
);

export const FilterPanelCard = styled(Box)(({ theme }) => ({
  padding: '14px 16px',
  borderRadius: '12px',
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.35)'
      : 'rgba(0, 0, 0, 0.04)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
}));

export const FilterSearchWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 10px',
  borderRadius: '8px',
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.04)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.08)'
      : '1px solid rgba(0, 0, 0, 0.08)',
  '& input': {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: theme.palette.white['06'],
    fontSize: '12px',
    width: '130px',
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.4)',
    },
  },
}));

export const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 20px',
  borderRadius: '12px',
  background:
    theme.palette.mode === 'dark'
      ? 'rgba(0, 0, 0, 0.2)'
      : 'rgba(0, 0, 0, 0.02)',
  border:
    theme.palette.mode === 'dark'
      ? '1px dashed rgba(255, 255, 255, 0.12)'
      : '1px dashed rgba(0, 0, 0, 0.12)',
  textAlign: 'center',
  gap: '10px',
  color: theme.palette.white['06'],
}));

export const TooltipChannelHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
  paddingBottom: '4px',
  borderBottom: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.08)'
  }`,
  letterSpacing: '0.3px',
}));

export const TooltipDotMarker = styled(Box)<{ color: string }>(
  ({ color }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: color,
    boxShadow: `0 0 6px ${color}`,
    flexShrink: 0,
  })
);

export const FilterCountTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.white['06'],
  opacity: 0.85,
  fontSize: '11px',
  whiteSpace: 'nowrap',
}));

export const ClearFilterChip = styled(FilterToggleChip)(({ theme }) => ({
  color: theme.palette.secondary['01'] || theme.palette.secondary.main,
  borderColor: theme.palette.secondary['01'] || theme.palette.secondary.main,
  '&:hover': {
    borderColor: theme.palette.secondary['01'] || theme.palette.secondary.main,
  },
}));

export const StyledRssiSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.primary.main,
  '& .MuiSlider-markLabel': {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '10px',
  },
  '& .MuiSlider-valueLabel': {
    background: theme.palette.primary.main,
    borderRadius: '6px',
  },
}));

export const LegendCardHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  paddingBottom: '6px',
  borderBottom: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.08)'
  }`,
}));

export const LegendChipLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.white['06'],
  maxWidth: 150,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const ChartTooltipCard = styled(CardContainer)(({ theme }) => ({
  maxWidth: '320px',
  padding: '10px 14px',
  borderRadius: '16px',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.12)'
      : '1px solid rgba(0, 0, 0, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
}));

/** Card used in the bottom legend popper for each network */
export const LegendNetworkCard = styled(CardContainer)({
  maxWidth: '320px',
  padding: '12px 14px',
  borderRadius: '20px',
});

/** Reusable label typography for filter panel rows (signal range label, channels label) */
export const FilterLabelTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.white['06'],
}));

/** Title of the bottom legend section (uppercase, letter-spaced) */
export const LegendTitleTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}));

/** SSID label inside each tooltip row (capped width, no wrap) */
export const TooltipNetworkLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.white['06'],
  maxWidth: 170,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

/** dBm value label inside each tooltip row */
export const TooltipSignalValue = styled(Typography)({
  fontWeight: 700,
  flexShrink: 0,
  fontSize: '11px',
});

/** Signal dBm chip label inside the bottom legend */
export const LegendSignalTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.white['06'],
  fontSize: '11px',
}));

/** Box wrapper for the RSSI slider section */
export const SliderBox = styled(Box)({
  flex: 1,
  minWidth: '240px',
});

/** Row header above the bottom network legend (title + optional actions) */
export const LegendHeaderRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

/** Wrapper for quick preset filter chips */
export const FilterPresetsWrapper = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

/** Wrapper for action buttons and counter on the right of filter bar */
export const FilterActionsWrapper = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '10px',
});

/** Main row inside filter details panel */
export const FilterPanelMainRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '20px',
  alignItems: 'center',
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

/** Header above RSSI slider */
export const SliderHeaderRow = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '4px',
});

/** Row containing channel toggle buttons */
export const ChannelsButtonRow = styled(Stack)({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '6px',
});

/** Wrapper for chips in the bottom legend */
export const LegendChipsWrapper = styled(Stack)({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '8px',
});

/** Row for an item in chart tooltip */
export const TooltipRowStack = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
});

/** Group containing dot indicator and network name in tooltip */
export const TooltipLabelGroup = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,
});
